import { useCallback, useMemo, useState } from 'react';

import { isUvInsertTextListYear } from '@shared/models/uv-inserts';

import type {
  ComponentGroups,
  PersonalisationSlot,
  ProductVariant
} from '../../context/personalisation-typedefs';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { buildPairedSlotModel } from '../../utilities/buildPairedSlotModel';
import { monthLabel } from '../../utilities/deriveStoryCardText';

// ─── Constants ────────────────────────────────────────────────────────────────

// Product copy from the Story of Us Figma (V1); "Meaningful Stone", not "Birthstone" (ALBAU-961).
const EMPTY_ROW_LABEL = 'Add Stone';
const DEFAULT_ROW_SUBHEADING = 'Meaningful Stone & Optional Moment';
const NO_TEXT_ROW_LABEL = 'Month only';
const SHEET_SUBTITLE = 'Choose Meaningful Stone, and Optional Moment';
const LIVE_LINE_BLANK_MOMENT = 'Blank prints the month only';
/** With a four-digit year the headline is "January 2017", so "the month only" would be untrue. */
const LIVE_LINE_BLANK_MOMENT_WITH_YEAR = 'Blank prints the date only';
const YEAR_ERROR_MESSAGE = 'Enter all four digits of the year, or leave it blank.';
const YEAR_REQUIRED_ERROR_MESSAGE = 'Enter all four digits of the year.';
const MOMENT_REQUIRED_ERROR_MESSAGE = 'Add your moment.';
/** A trailing "(optional)" in an admin heading, any case, so the picker can write its own " (Optional)". */
const OPTIONAL_HEADING_SUFFIX = /\s*\(optional\)\s*$/i;

const DEFAULT_STONE_COLUMNS = 3;

/** Separates the year and moment on a row's sub-line. */
const SUB_LINE_SEPARATOR = ' · ';

/** The spaced dash between month and stone in a variant title ("January – Garnet"). */
const STONE_TITLE_SEPARATOR = /\s+[–—-]\s+/;

// ─── Public types ─────────────────────────────────────────────────────────────

/** Shape of an alphanumeric slot value object (text plus optional font/translation). */
export interface TextSlotValue {
  text?: string;
  font?: string;
  translatedText?: string;
}

/** A stone tile in the picker grid. */
export interface StoneChoice {
  variantId: string;
  title: string;
  /** The tile's text lines: ["January", "Garnet"] for "January – Garnet", else [title]. */
  titleLines: string[];
  /** The tile's accessible name: "January, Garnet" when the title splits, else the title. */
  accessibleName: string;
  imageUrl: string | null;
  imageAlt: string;
  isAvailable: boolean;
  isSelected: boolean;
}

/** One rendered row of the step: a stone and its optional year and moment. */
export interface PairedRowViewModel {
  index: number;
  /** One-based position shown to the customer. */
  stoneNumber: number;
  componentSlot: PersonalisationSlot;
  /** The moment slot. */
  textSlot: PersonalisationSlot | null;
  yearSlot: PersonalisationSlot | null;
  isFilled: boolean;
  variantId: string | null;
  variantTitle: string | null;
  variantImageUrl: string | null;
  /** The saved year, or '' unless it is the four digits the card will print. */
  yearText: string;
  momentText: string;
  primaryLabel: string;
  secondaryLabel: string;
  /**
   * What a filled row holds, read as a sentence — "January – Garnet, January 2017, Day we met",
   * i.e. the stone and then what the card will print (the "Month Year" headline only when there is
   * a printable year, since the stone title already names the month) — or null on an empty row,
   * whose button the visible text names.
   */
  accessibleLabel: string | null;
  /** The filled row's Edit control name — "Edit stone 1: January – Garnet, …" — or '' when empty. */
  editLabel: string;
  removeLabel: string;
}

export interface PairedSlotViewModelResult {
  rows: PairedRowViewModel[];
  /**
   * The slot whose `uiHeading`/`uiSubheading`/`helpPage` head the step, or null when
   * there is nothing to say and the step must render exactly as it did before.
   */
  headerSlot: PersonalisationSlot | null;
  totalRows: number;
  filledRowCount: number;
  openRowIndex: number | null;
  isSheetOpen: boolean;
  sheetRow: PairedRowViewModel | null;
  sheetTitle: string;
  sheetSubtitle: string;
  stoneChoices: StoneChoice[];
  stoneColumns: number;
  /**
   * Slot config handed to the year's PersonalisationAlphanumericSlot, with its heading, subheading,
   * help page and input label blanked: the picker renders the year's one label (`yearLabel`).
   */
  yearSlotConfig: PersonalisationSlot | null;
  /** The year input's label: the slot's `uiHeading`, else "Add Year n" (required) or "Add Year n (Optional)". */
  yearLabel: string;
  /**
   * Slot config handed to the moment's PersonalisationAlphanumericSlot, with its heading, subheading,
   * help page and input label blanked: the picker renders the moment's one label (`momentLabel`).
   */
  momentSlotConfig: PersonalisationSlot | null;
  /** The moment input's label: the slot's `uiHeading`, else "Add Moment n" (required) or "Add Moment n (Optional)". */
  momentLabel: string;
  pendingVariantId: string | null;
  pendingYearValue: TextSlotValue | string;
  pendingMomentValue: TextSlotValue | string;
  /**
   * Inline error for a 1–3 digit year, or null. Shown only once the customer has left the year
   * input, or tried to save, with the year half-typed - never mid-keystroke - and cleared the
   * moment the year is four digits or empty. `canSave` is false the whole time the year is
   * half-typed, whether or not this is showing.
   */
  yearError: string | null;
  /**
   * Inline error for a required moment left empty, or null. Shown once the customer has left the
   * moment input, or tried to save, and cleared the moment they type. `canSave` is false the whole
   * time a required moment is empty, whether or not this is showing.
   */
  momentError: string | null;
  liveLineText: string;
  canSave: boolean;
  openRow: (rowIndex: number) => void;
  cancelSheet: () => void;
  selectStone: (variantId: string) => void;
  changeYear: (newValue: unknown) => void;
  /** Call when the year input loses focus: a half-typed year then shows its error. */
  blurYear: () => void;
  /** Call when the moment input loses focus: an empty required moment then shows its error. */
  blurMoment: () => void;
  changeMoment: (newValue: unknown) => void;
  saveRow: () => void;
  removeRow: (rowIndex: number) => void;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

/** Extracts the trimmed text from an alphanumeric slot value, which may be a plain string. */
function getText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object' && 'text' in value) {
    const { text } = value as TextSlotValue;
    return typeof text === 'string' ? text.trim() : '';
  }
  return '';
}

/** A pending input value as stored state: the input's own object, a string, or '' for nothing. */
function toPendingValue(value: unknown): TextSlotValue | string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value as TextSlotValue;
  return '';
}

/**
 * The value written to a text slot, trimmed: the input's own object (font, translation) kept whole
 * with its text and translation trimmed, or a string wrapped as `{ text }`. Untrimmed, a trailing
 * space reached the cart's `Product Moment n` although the card, which trims, never printed it.
 */
function toSelectionValue(value: TextSlotValue | string): TextSlotValue {
  if (typeof value !== 'object' || value === null) return { text: getText(value) };

  return {
    ...value,
    text: getText(value),
    ...(typeof value.translatedText === 'string' && { translatedText: value.translatedText.trim() })
  };
}

/** A year the card prints: exactly four digits, else ''. Same rule as `text-list-rows.ts`. */
function printableYear(text: string): string {
  return isUvInsertTextListYear(text) ? text : '';
}

/** "Month Year" when there is a printable year, else "Month" — the card's headline rule. */
function headlineFor(month: string, year: string): string {
  return year ? `${month} ${year}` : month;
}

/**
 * A text slot config that renders only the input, its counter and its restrictions: no slot
 * header and no label of its own, because the picker labels the input itself.
 */
function withoutOwnLabel(slot: PersonalisationSlot | null): PersonalisationSlot | null {
  if (!slot) return null;
  return { ...slot, uiHeading: '', uiSubheading: '', helpPage: null, inputLabel: '' } as PersonalisationSlot;
}

/**
 * The one label the picker shows above a year or moment input: the slot's `uiHeading`, else
 * "Add Year n" / "Add Moment n". " (Optional)" is added when the slot is not required; a heading
 * that already ends in "(optional)", in any case, has it replaced, so it always reads with a
 * capital O and never twice. Both inputs use this, so a heading set in the admin never stacks with
 * a second label (ALBAU-956).
 */
function rowFieldLabel(slot: PersonalisationSlot | null, name: string, stoneNumber: number): string {
  if (!slot) return '';
  const label = hasText(slot.uiHeading)
    ? (slot.uiHeading as string).replace(OPTIONAL_HEADING_SUFFIX, '')
    : `Add ${name} ${stoneNumber}`;
  return isRequiredSlot(slot) ? label : `${label} (Optional)`;
}

/** Liquid sends booleans as booleans or 'true'/'false'. */
export function isRequiredSlot(slot: PersonalisationSlot | null | undefined): boolean {
  return slot?.isRequired === true || (slot?.isRequired as unknown) === 'true';
}

/** Splits "January – Garnet" into its month and stone lines; a title with no spaced dash stays whole. */
function splitStoneTitle(title: string): string[] {
  const separator = STONE_TITLE_SEPARATOR.exec(title);
  if (!separator) return [title];
  return [title.slice(0, separator.index), title.slice(separator.index + separator[0].length)];
}

/**
 * True when a metaobject text field holds real copy.
 *
 * Blank fields come back as '', null or the literal string 'null', the same three
 * cases the alphanumeric slot already guards against.
 */
function hasText(value: unknown): boolean {
  return typeof value === 'string' && value !== '' && value !== 'null';
}

/** Variant titles are "Default Title" when the birthstone lives on its own product. */
function getVariantTitle(variant: ProductVariant | undefined): string {
  if (!variant) return '';
  const productTitle = (variant as ProductVariant & { product?: { title?: string } }).product?.title;
  return variant.title === 'Default Title' && productTitle ? productTitle : variant.title;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * View model for PAIRED slot mode ("Your stones, your moments").
 *
 * Each COMPONENT slot is joined with the ALPHANUMERIC slots that share its orderIndex — a
 * numbers-only year and a moment (see `buildPairedSlotModel`) — and rendered as one row. Tapping a
 * row opens the picker, whose unsaved edits live only here; saving writes every slot in the row
 * through the ordinary `updateSelections` API, so the cart, pricing and line item properties are
 * identical to a FIXED step with the same slots. The keepsake card mirrors those slots through
 * `deriveStoryCardText`.
 *
 * @param options - Options object.
 * @param options.slots - Every slot on the step.
 * @param options.componentGroups - Resolved component groups from the context.
 * @returns The rows, sheet state and handlers for PersonalisationPairedSlot.
 */
export function usePairedSlotViewModel({
  slots,
  componentGroups
}: {
  slots: PersonalisationSlot[];
  componentGroups: ComponentGroups | null;
}): PairedSlotViewModelResult {
  const { selections, updateSelections, variants } = usePersonalisationContext();

  const { rows: pairedRows } = useMemo(() => buildPairedSlotModel({ slots }), [slots]);

  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);
  const [pendingVariantId, setPendingVariantId] = useState<string | null>(null);
  const [pendingYearValue, setPendingYearValue] = useState<TextSlotValue | string>('');
  const [pendingMomentValue, setPendingMomentValue] = useState<TextSlotValue | string>('');
  // Whether a half-typed year's error is visible: set on blur or a save attempt, cleared once the
  // year is complete or empty, so retyping waits for the next blur before nagging again.
  const [isYearErrorRevealed, setIsYearErrorRevealed] = useState(false);
  // The same for a required moment left empty.
  const [isMomentErrorRevealed, setIsMomentErrorRevealed] = useState(false);

  const selectionSlots = useMemo(() => selections?.slots ?? [], [selections?.slots]);

  /** Returns the saved selection value for a slot id, or null. */
  const getSelectedValue = useCallback(
    (slotId: string | undefined): unknown => {
      if (!slotId) return null;
      return selectionSlots.find((selection) => selection.id === slotId)?.selected ?? null;
    },
    [selectionSlots]
  );

  /**
   * Persistent explanatory copy for the whole step, taken from the first stone slot.
   *
   * Every other slot type renders PersonalisationSlotHeader; PAIRED had no way to show
   * standing copy, so `uiSubheading` only ever appeared as an unfilled row's sub-line and
   * vanished once a stone was chosen. The header is mounted only when the slot actually
   * carries a subheading or a help page, so a step configured like today's renders
   * identically - which matters because this component is shared with every other product.
   */
  const headerSlot = useMemo<PersonalisationSlot | null>(() => {
    const firstComponentSlot = pairedRows[0]?.componentSlot ?? null;
    if (!firstComponentSlot) return null;
    const hasHeaderContent = hasText(firstComponentSlot.uiSubheading) || Boolean(firstComponentSlot.helpPage);
    return hasHeaderContent ? firstComponentSlot : null;
  }, [pairedRows]);

  const rows = useMemo<PairedRowViewModel[]>(() => {
    const totalRows = pairedRows.length;

    return pairedRows.map(({ index, componentSlot, textSlot, yearSlot }) => {
      const stoneNumber = index + 1;
      const selectedVariantId = getSelectedValue(componentSlot.id);
      const variantId = typeof selectedVariantId === 'string' && selectedVariantId ? selectedVariantId : null;
      const variant = variantId ? variants?.get?.(variantId) : undefined;
      const variantTitle = variantId ? getVariantTitle(variant) || variantId : null;
      const isFilled = variantId !== null;
      const yearText = printableYear(getText(getSelectedValue(yearSlot?.id)));
      const momentText = getText(getSelectedValue(textSlot?.id));

      // Filled: "Stone, Month Year, Moment" — the stone, then what the card prints. The headline is
      // only added with a printable year: on its own the month repeats the stone title's own.
      const month = isFilled ? monthLabel({ variant }) : '';
      const accessibleLabel = isFilled
        ? [variantTitle, month && yearText && headlineFor(month, yearText), momentText].filter(Boolean).join(', ')
        : null;

      return {
        index,
        stoneNumber,
        componentSlot,
        textSlot,
        yearSlot,
        isFilled,
        variantId,
        variantTitle,
        variantImageUrl: variant?.image?.url ?? null,
        yearText,
        momentText,
        primaryLabel: isFilled ? (variantTitle as string) : EMPTY_ROW_LABEL,
        // The row that sourced the header falls back to the default sub-line: printing
        // `uiSubheading` here too would repeat the header's sentence directly underneath it
        // and give row 1 copy its siblings do not have. Rows 2+ keep their own.
        secondaryLabel: isFilled
          ? [yearText, momentText].filter(Boolean).join(SUB_LINE_SEPARATOR) || NO_TEXT_ROW_LABEL
          : (componentSlot === headerSlot ? '' : componentSlot.uiSubheading) ||
            `${stoneNumber}/${totalRows} ${DEFAULT_ROW_SUBHEADING}`,
        accessibleLabel,
        editLabel: isFilled ? `Edit stone ${stoneNumber}: ${accessibleLabel}` : '',
        removeLabel: `Remove stone ${stoneNumber}`
      };
    });
  }, [pairedRows, getSelectedValue, variants, headerSlot]);

  const sheetRow = openRowIndex === null ? null : rows[openRowIndex] ?? null;

  /** The single component group the stones are chosen from. */
  const stoneGroupId = sheetRow?.componentSlot.allowedComponentGroups?.[0] ?? null;
  const stoneGroup = stoneGroupId ? componentGroups?.get?.(stoneGroupId) ?? null : null;

  const stoneChoices = useMemo<StoneChoice[]>(() => {
    if (!stoneGroup) return [];

    return (stoneGroup.productVariantIds ?? [])
      .map((variantId) => variants?.get?.(variantId))
      .filter((variant): variant is ProductVariant => Boolean(variant))
      .map((variant) => {
        const title = getVariantTitle(variant) || variant.id;
        const titleLines = splitStoneTitle(title);
        return {
          variantId: variant.id,
          title,
          titleLines,
          // Ticket AC3: the tile reads "January, Garnet", the two lines it shows.
          accessibleName: titleLines.join(', '),
          imageUrl: variant.image?.url ?? null,
          imageAlt: title,
          isAvailable: variant.availableForSale !== false,
          isSelected: pendingVariantId === variant.id
        };
      });
  }, [stoneGroup, variants, pendingVariantId]);

  const isYearRequired = isRequiredSlot(sheetRow?.yearSlot);
  const isMomentRequired = isRequiredSlot(sheetRow?.textSlot);

  const yearSlotConfig = useMemo(() => withoutOwnLabel(sheetRow?.yearSlot ?? null), [sheetRow?.yearSlot]);
  const momentSlotConfig = useMemo(() => withoutOwnLabel(sheetRow?.textSlot ?? null), [sheetRow?.textSlot]);
  const yearLabel = sheetRow ? rowFieldLabel(sheetRow.yearSlot, 'Year', sheetRow.stoneNumber) : '';
  const momentLabel = sheetRow ? rowFieldLabel(sheetRow.textSlot, 'Moment', sheetRow.stoneNumber) : '';

  const pendingYearText = yearSlotConfig ? getText(pendingYearValue) : '';
  const pendingMomentText = getText(pendingMomentValue);

  // The slot's own restrictions keep non-digits out at the keystroke; this catches the length.
  // A 1–3 digit year would ship as `Year n` in the cart while the card printed none.
  const isYearIncomplete = pendingYearText !== '' && !isUvInsertTextListYear(pendingYearText);
  // A required year also has to be filled in at all.
  const isYearMissing = Boolean(yearSlotConfig) && isYearRequired && pendingYearText === '';
  const isYearInvalid = isYearIncomplete || isYearMissing;
  let yearError: string | null = null;
  if (isYearInvalid && isYearErrorRevealed) {
    yearError = isYearRequired ? YEAR_REQUIRED_ERROR_MESSAGE : YEAR_ERROR_MESSAGE;
  }

  const isMomentMissing = Boolean(momentSlotConfig) && isMomentRequired && pendingMomentText === '';
  const momentError = isMomentMissing && isMomentErrorRevealed ? MOMENT_REQUIRED_ERROR_MESSAGE : null;

  const liveLineText = useMemo<string>(() => {
    // The moment panel, and with it this line, only shows once a stone is chosen.
    if (!pendingVariantId) return '';

    // What the card actually prints is the month (and a four-digit year) — `monthLabel`, the
    // derive's own helper, is the single source of truth for the month, so this preview cannot
    // drift from the keepsake card the way the full variant title did (ALBAU-865). The row list
    // still names the whole stone; only this line is a promise about the print.
    const pendingMonth = monthLabel({ variant: variants?.get?.(pendingVariantId) }) || pendingVariantId;
    const pendingYear = printableYear(pendingYearText);
    const headline = headlineFor(pendingMonth, pendingYear);
    const blankMoment = pendingYear ? LIVE_LINE_BLANK_MOMENT_WITH_YEAR : LIVE_LINE_BLANK_MOMENT;

    return `On the card: ★ ${headline} - ${pendingMomentText || blankMoment}`;
  }, [pendingVariantId, pendingYearText, pendingMomentText, variants]);

  const openRow = useCallback(
    (rowIndex: number) => {
      const row = rows[rowIndex];
      if (!row) return;

      // Seed the sheet from what is already saved, so reopening a filled row
      // shows the customer's existing stone, year and moment.
      setPendingVariantId(row.variantId);
      setPendingYearValue(toPendingValue(getSelectedValue(row.yearSlot?.id)));
      setPendingMomentValue(toPendingValue(getSelectedValue(row.textSlot?.id)));
      setIsYearErrorRevealed(false);
      setIsMomentErrorRevealed(false);
      setOpenRowIndex(rowIndex);
    },
    [rows, getSelectedValue]
  );

  const cancelSheet = useCallback(() => {
    setOpenRowIndex(null);
    setPendingVariantId(null);
    setPendingYearValue('');
    setPendingMomentValue('');
    setIsYearErrorRevealed(false);
    setIsMomentErrorRevealed(false);
  }, []);

  const selectStone = useCallback((variantId: string) => {
    setPendingVariantId(variantId);
  }, []);

  const changeYear = useCallback(
    (newValue: unknown) => {
      const text = getText(newValue);
      setPendingYearValue(toPendingValue(newValue));
      if (isUvInsertTextListYear(text) || (text === '' && !isYearRequired)) setIsYearErrorRevealed(false);
    },
    [isYearRequired]
  );

  const blurYear = useCallback(() => {
    if (isYearInvalid) setIsYearErrorRevealed(true);
  }, [isYearInvalid]);

  const changeMoment = useCallback((newValue: unknown) => {
    setPendingMomentValue(toPendingValue(newValue));
    if (getText(newValue) !== '') setIsMomentErrorRevealed(false);
  }, []);

  const blurMoment = useCallback(() => {
    if (isMomentMissing) setIsMomentErrorRevealed(true);
  }, [isMomentMissing]);

  const canSave = pendingVariantId !== null && !isYearInvalid && !isMomentMissing;

  const saveRow = useCallback(() => {
    if (isYearInvalid || isMomentMissing) {
      // Show every reason Continue is stopping, not just the first.
      if (isYearInvalid) setIsYearErrorRevealed(true);
      if (isMomentMissing) setIsMomentErrorRevealed(true);
      return;
    }
    if (!sheetRow || !pendingVariantId || !canSave) return;

    const { componentSlot, yearSlot, textSlot } = sheetRow;
    const componentGroupId = componentSlot.allowedComponentGroups?.[0] ?? null;

    updateSelections({
      slotId: componentSlot.id,
      itemSelected: pendingVariantId,
      ...(componentGroupId != null && { componentGroupId })
    });

    if (yearSlot) {
      updateSelections({
        slotId: yearSlot.id,
        itemSelected: pendingYearText ? toSelectionValue(pendingYearValue) : null
      });
    }

    if (textSlot) {
      updateSelections({
        slotId: textSlot.id,
        itemSelected: pendingMomentText ? toSelectionValue(pendingMomentValue) : null
      });
    }

    cancelSheet();
  }, [
    sheetRow,
    pendingVariantId,
    canSave,
    isYearInvalid,
    isMomentMissing,
    pendingYearValue,
    pendingYearText,
    pendingMomentValue,
    pendingMomentText,
    updateSelections,
    cancelSheet
  ]);

  const removeRow = useCallback(
    (rowIndex: number) => {
      const row = rows[rowIndex];
      if (!row) return;

      const componentGroupId = row.componentSlot.allowedComponentGroups?.[0] ?? null;

      updateSelections({
        slotId: row.componentSlot.id,
        itemSelected: null,
        ...(componentGroupId != null && { componentGroupId })
      });

      for (const textSlot of [row.yearSlot, row.textSlot]) {
        if (textSlot) updateSelections({ slotId: textSlot.id, itemSelected: null });
      }
    },
    [rows, updateSelections]
  );

  return {
    rows,
    headerSlot,
    totalRows: rows.length,
    filledRowCount: rows.filter((row) => row.isFilled).length,
    openRowIndex,
    isSheetOpen: sheetRow !== null,
    sheetRow,
    sheetTitle: sheetRow ? `Stone ${sheetRow.stoneNumber} of ${rows.length}` : '',
    sheetSubtitle: SHEET_SUBTITLE,
    stoneChoices,
    stoneColumns: Number(sheetRow?.componentSlot.columns) || DEFAULT_STONE_COLUMNS,
    yearSlotConfig,
    yearLabel,
    momentSlotConfig,
    momentLabel,
    pendingVariantId,
    pendingYearValue,
    pendingMomentValue,
    yearError,
    momentError,
    liveLineText,
    canSave,
    openRow,
    cancelSheet,
    selectStone,
    changeYear,
    blurYear,
    blurMoment,
    changeMoment,
    saveRow,
    removeRow
  };
}
